/**
 * useSessionExpiry — Live countdown for Tripjack /review session validity.
 *
 * Tripjack's /review response carries `conditions.st` (session-validity
 * seconds). The backend translates that to `form_conditions.session_expires_at`
 * (ISO-8601) per Audit §6.6 (May-2026). When the supplier is the
 * authoritative TTL holder, we shrink our local 30-min hold timer to whatever
 * is shorter and disable the Pay/Continue CTAs when the session is dead.
 *
 * @param formConditions  The `form_conditions` slice surfaced by /review.
 * @returns `{ expiresAt: Date|null, secondsRemaining: number|null, isExpired: boolean, hasSupplierTtl: boolean }`
 */
import { useEffect, useState } from 'react';

export default function useSessionExpiry(formConditions) {
  const rawExpiresAt = formConditions && formConditions.session_expires_at;
  const expiresAt = rawExpiresAt ? new Date(rawExpiresAt) : null;
  const isValid = expiresAt && !Number.isNaN(expiresAt.getTime());

  const computeRemaining = () => {
    if (!isValid) return null;
    return Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
  };

  const [secondsRemaining, setSecondsRemaining] = useState(computeRemaining);

  useEffect(() => {
    if (!isValid) {
      setSecondsRemaining(null);
      return undefined;
    }
    setSecondsRemaining(computeRemaining());
    const id = setInterval(() => {
      setSecondsRemaining(computeRemaining());
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawExpiresAt]);

  return {
    expiresAt: isValid ? expiresAt : null,
    secondsRemaining,
    isExpired: isValid && secondsRemaining === 0,
    hasSupplierTtl: !!isValid,
  };
}

export function formatSecondsAsClock(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return '';
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}
